import { useMemo } from 'react'
import styles from './PriceDisplay.module.scss'
import classNames from 'classnames'
import { PriceDisplayProps } from './types'
import { formatAmount } from 'utils/format'
import { getTotalSimulatedPrice } from 'utils/price'
import { useStoreSelector } from 'hooks/store'
import { LoadingSkeleton } from '@boltenergy-be/design-system'
import { selectSimulationLoading } from 'store/customer/slice'

const PriceDisplay = ({
  className,
  classNameWrapper,
  isLoading,
  proposition,
  fixedPrices,
  twoLines,
  textColor = 'blue',
  label,
  forcedAmount,
  counterpartProposition
}: PriceDisplayProps) => {
  // Redux store
  const { chosenSimulationType } = useStoreSelector((store) => store.customer.inputs.simulation)
  const { needsGas } = useStoreSelector((store) => store.customer.inputs.deliveryAddress)
  const simulationLoading = useStoreSelector(selectSimulationLoading)

  // Constants
  const loading = isLoading || simulationLoading

  // The price to show
  const priceToShow = useMemo<number>(() => {
    if (forcedAmount) {
      return forcedAmount
    }

    if (proposition) {
      return getTotalSimulatedPrice(proposition?.price)
    }

    return (Number(fixedPrices?.electricity) || 0) + (Number(fixedPrices?.gas) || 0)
  }, [fixedPrices?.electricity, fixedPrices?.gas, forcedAmount, needsGas, proposition, chosenSimulationType])

  if (!proposition && !fixedPrices && !forcedAmount) {
    return null
  }

  const priceWithoutPromo = !forcedAmount
    ? counterpartProposition
      ? counterpartProposition?.priceWithoutPromo || getTotalSimulatedPrice(counterpartProposition.price)
      : proposition?.priceWithoutPromo
    : null

  return (
    <div className={classNameWrapper || ''}>
      {priceWithoutPromo && (
        <div className={classNames(styles.price, styles['without-promo'], styles[textColor], className)}>
          {loading ? (
            <LoadingSkeleton className={classNames(styles['promo-loader'], { [styles.center]: twoLines })}>
              <LoadingSkeleton.Rectangle width="100%" height={22} className="mx-auto" />
            </LoadingSkeleton>
          ) : (
            <>
              <span>&euro;</span>
              <strong>{formatAmount(priceWithoutPromo)}</strong>
            </>
          )}
        </div>
      )}

      <div className={classNames(styles.price, styles[textColor], className)}>
        {loading ? (
          <LoadingSkeleton className={styles.loader}>
            <LoadingSkeleton.Rectangle width="100%" height={26} />
          </LoadingSkeleton>
        ) : (
          <>
            <span>&euro;</span>
            <strong>{formatAmount(priceToShow)}</strong>
          </>
        )}
        {twoLines && <br />}
        <small>{label}</small>
      </div>
    </div>
  )
}

export default PriceDisplay
